import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, BoxProps, Button, FormLabel, useTheme } from '@mui/material';
import { useField } from 'formik';

import { InputBaseField } from '../common/form/FormInput';

export type OptionTypes = {
    value: string;
    label: string;
};

type CustomCounterProps = {
    name: string;
    value: number;
    onChange: (count: number) => void;
    label?: string;
    boxStyling?: BoxProps;
    error?: string;
    placeholder?: string;
    min?: number;
    max?: number;
    currency?: string;
    percentage?: boolean;
    disabled?: boolean;
};

export const CustomCounter: React.FC<CustomCounterProps> = ({
    value,
    onChange,
    label,
    boxStyling,
    error,
    placeholder,
    name,
    min,
    max,
    currency,
    percentage,
    disabled,
}) => {
    const [{ onBlur }, { touched }, helpers] = useField(name);
    const [count, setCount] = useState<number>(value);
    const { palette } = useTheme();

    // Update the field value in the parent component
    useEffect(() => {
        if (!Number.isNaN(count)) {
            onChange(count);
        }
    }, [count]);

    // Increment the count value by +1
    const increment = () => {
        setCount((prevCount) => {
            return !Number.isNaN(prevCount) && (prevCount as unknown as string) !== ''
                ? Number.parseFloat(prevCount.toString()) + 1.0
                : 1.0;
        });
    };

    // Decrement the count value by -1
    const decrement = () => {
        if (count === 0) {
            return;
        }
        if (max && count >= max) {
            return max;
        }

        setCount((prevCount) => {
            return !Number.isNaN(prevCount) && (prevCount as unknown as string) !== ''
                ? Number.parseFloat(prevCount.toString()) - 1.0
                : 0.0;
        });
    };

    const borderColor = error ? 'red' : palette.primary.main;
    return (
        <Box display="flex" flexDirection="column" {...boxStyling}>
            {label && (
                <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                    {label}
                </FormLabel>
            )}

            <Box
                display="flex"
                flexDirection="row"
                border={`0px solid ${borderColor}`}
                overflow="hidden"
                gap={2}
                alignItems="center"
            >
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor={palette.primary.main}
                    height="32px"
                    width="32px"
                    borderRadius="8px"
                    color="white"
                    fontSize="32px"
                >
                    <Button
                        sx={{
                            width: '32px',
                            height: '100%',
                            padding: 0,
                            color: 'white',
                            fontSize: '32px',
                        }}
                        onClick={decrement}
                        disabled={disabled}
                    >
                        <RemoveIcon />
                    </Button>
                </Box>
                <InputBaseField
                    value={count}
                    onChange={(e) => {
                        if (Number(e.target.value) <= 0 || Number.isNaN(Number(e.target.value))) {
                            return setCount(0);
                        }
                        if (min && Number(e.target.value) <= min) {
                            return setCount(min);
                        }
                        if (max && Number(e.target.value) >= max) {
                            return setCount(max);
                        } else {
                            setCount(Number(e.target.value));
                        }
                    }}
                    onBlur={onBlur}
                    percentage={percentage || placeholder === '%'}
                    currency={currency || placeholder === currency ? currency : null}
                    placeholder={placeholder || label}
                    type="text"
                    name={name}
                    style={{
                        paddingLeft: '0px',
                        paddingRight: '0px',
                    }}
                    inputProps={{
                        className: 'MuiInputBase-input',
                        style: {
                            textAlign: 'center',
                            borderWidth: 0,
                            padding: '16px 14px',
                            width: '100%',
                            borderBottom: `1px solid ${palette.primary.main}`,
                            position: 'relative',
                        },
                        onFocus: (e) => {
                            e.target.style.borderBottom = `2px solid ${palette.primary.main}`;
                        },
                        onBlur: (e) => {
                            e.target.style.borderBottom = '1px solid rgba(0, 0, 0, 0.42)';
                        },
                    }}
                    sx={{
                        '&.MuiInputBase-root': {
                            border: 'none !important',
                            '&:hover': {
                                border: 'none !important',
                            },
                            '&.Mui-focused': {
                                border: 'none !important',
                            },
                        },
                        '& input[type=number]': {
                            '-moz-appearance': 'textfield',
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: (props) => props.palette.primary.main,
                            },
                            '&:hover fieldset': {
                                borderColor: (props) => props.palette.primary.main,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: (props) => props.palette.primary.main,
                            },
                        },
                    }}
                    disabled={disabled}
                />
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    bgcolor={palette.primary.main}
                    height="32px"
                    width="32px"
                    borderRadius="8px"
                    color="white"
                    fontSize="32px"
                >
                    <Button
                        sx={{
                            width: '32px',
                            height: '100%',
                            padding: 0,
                            color: 'white',
                            fontSize: '32px',
                        }}
                        onClick={increment}
                        disabled={disabled}
                    >
                        <AddIcon />
                    </Button>
                </Box>
            </Box>
            {error && touched && <label className="error">{error}</label>}
        </Box>
    );
};
